import React from "react";
import { useSkin } from "../../skins/SkinContext";
import { SkRect, SkEllipse, SkCircle, SkPolygon, SkLine } from "../../prim";
import type { SkinSceneProps } from "./SkinParkScene";

/*
 * Anivaze v3 — skin-aware BREATHING / lungs scene. A child's chest expands on the
 * inhale (air flows in, lungs inflate, heart beats), contracts on the exhale.
 * Composed from skinnable primitives → re-renders in every style.
 */
export const SkinBreathingScene: React.FC<SkinSceneProps> = ({ opacity = 1, t = 0, label = "Breathe in, breathe out!" }) => {
  const skin = useSkin();
  const cx = 400;
  const breath = Math.sin((t % 4) / 4 * Math.PI * 2); // -1..1 over 4s
  const inhale = Math.max(0, breath); // 0..1
  const isIn = breath > 0;
  const lung = 1 + inhale * 0.22;
  const chest = 1 + inhale * 0.07;
  const heartBeat = 1 + Math.max(0, Math.sin(t * 7)) * 0.12;
  const airColor = isIn ? "#60a5fa" : "#cbd5e1";

  return (
    <g opacity={opacity}>
      <rect width={800} height={450} fill={skin.surface} />

      {/* air dots flowing in/out through the nose */}
      {Array.from({ length: 4 }, (_, i) => {
        const phase = (t * 1.2 + i * 0.5) % 1;
        const y = isIn ? 70 + phase * 90 : 160 - phase * 90;
        return <SkCircle key={i} cx={cx + (i - 1.5) * 12} cy={y} r={4} fill={airColor} seed={870 + i} />;
      })}

      {/* head */}
      <SkCircle cx={cx} cy={150} r={42} fill="#fcd34d" seed={860} />
      <SkCircle cx={cx - 13} cy={146} r={4} fill="#1f2937" seed={861} />
      <SkCircle cx={cx + 13} cy={146} r={4} fill="#1f2937" seed={862} />
      <SkLine x1={cx - 2} y1={156} x2={cx + 2} y2={160} stroke="#b45309" strokeWidth={2} seed={863} />
      <SkRect x={cx - 9} y={166} w={18} h={6} rx={3} fill="#ef4444" seed={864} />

      {/* torso (chest expands on inhale) */}
      <g transform={`translate(${cx} 300) scale(${chest} 1) translate(${-cx} -300)`}>
        <SkRect x={cx - 80} y={200} w={160} h={170} rx={28} fill="#60a5fa" seed={865} />
        {/* lungs */}
        <g transform={`translate(${cx - 36} 270) scale(${lung})`}>
          <SkEllipse cx={0} cy={0} rx={26} ry={42} fill="#fda4af" seed={866} />
        </g>
        <g transform={`translate(${cx + 36} 270) scale(${lung})`}>
          <SkEllipse cx={0} cy={0} rx={26} ry={42} fill="#fda4af" seed={867} />
        </g>
        {/* windpipe */}
        <SkLine x1={cx} y1={210} x2={cx} y2={258} stroke="#f8b4c0" strokeWidth={6} seed={868} />
        {/* heart */}
        <g transform={`translate(${cx} 278) scale(${heartBeat})`}>
          <SkPolygon points={[[0, 10], [-12, -4], [-6, -12], [0, -6], [6, -12], [12, -4]]} fill="#ef4444" seed={869} />
        </g>
      </g>

      {/* In / Out badge */}
      <g transform="translate(620 150)">
        <rect x={-46} y={-22} width={92} height={44} rx={22} fill="rgba(255,255,255,0.9)" stroke="#cbd5e1" strokeWidth={1.5} />
        <text x={0} y={6} textAnchor="middle" fill={skin.textInk} fontSize={20} fontWeight={800} fontFamily={skin.fontFamily}>{isIn ? "In…" : "Out…"}</text>
      </g>

      <g transform="translate(400 44)">
        <rect x={-220} y={-22} width={440} height={40} rx={20} fill="rgba(255,255,255,0.88)" stroke="#cbd5e1" strokeWidth={1.5} />
        <text x={0} y={4} textAnchor="middle" fill={skin.textInk} fontSize={16} fontWeight={700} fontFamily={skin.fontFamily}>{label}</text>
      </g>
    </g>
  );
};
